import { useCallback, useMemo } from "react";
import { MfaMethod } from "../types";
import { CredentialFormData } from "./useCredentialForm";

interface UseFieldValidationProps {
  formData: CredentialFormData;
  isEditMode: boolean;
  isPasswordEditing: boolean;
  isSecretKeyEditing: boolean;
  mfaMethod: MfaMethod;
  touched: Record<string, boolean>;
  markTouched: (field: string) => void;
  markFieldsTouched: (fields: string[]) => void;
  handleInputChange: (field: string, value: string) => void;
}

/**
 * Computes per-field validation errors and exposes the helpers used by
 * the orchestrator + field components to wire errors into the UI.
 *
 * Required fields:
 * - Portal ID, Portal Name, Portal URL, Username — always required.
 * - Facility Name in OncoEmr — required only when Portal ID is `oncoemr`,
 *   since that portal needs a facility scope to disambiguate logins.
 * - Location ID in OncoEmr — required only when Portal ID is `oncoemr`.
 * - Password — required in add mode, and in edit mode only after the user
 *   has clicked the pencil to overwrite the stored value (otherwise the
 *   existing encrypted value is reused on save).
 * - Secret Key — required only when MFA is "Yes" and the secret-key branch
 *   is active, following the same edit-pencil rule as Password.
 * - Email — required only when MFA is "Yes" and the OTP-email branch is
 *   active.
 * - Max Concurrency — required whenever the field is visible, i.e. unless
 *   MFA is "Yes"; must be a positive integer (the field is hidden and
 *   irrelevant while MFA is on).
 * - MFA Present, Is Active — radio groups must have a value chosen.
 *
 * The `touched` map is owned by `useTouched`; this hook only reads from
 * it (via `errorFor`) and delegates writes through the injected
 * `markTouched` / `markFieldsTouched` callbacks.
 */
export const useFieldValidation = ({
  formData,
  isEditMode,
  isPasswordEditing,
  isSecretKeyEditing,
  mfaMethod,
  touched,
  markTouched,
  markFieldsTouched,
  handleInputChange,
}: UseFieldValidationProps) => {
  const fieldErrors = useMemo(() => {
    const isBlank = (value?: string) => !value || value.trim() === "";
    const errors: Record<string, string> = {};
    if (isBlank(formData.portal_id)) errors.portal_id = "Portal ID is required";
    if (isBlank(formData.portal_name))
      errors.portal_name = "Portal Name is required";
    if (isBlank(formData.portal_url))
      errors.portal_url = "Portal URL is required";
    if (isBlank(formData.username)) errors.username = "Username is required";
    if (formData.portal_id === "oncoemr" && isBlank(formData.facility_name))
      errors.facility_name = "Facility Name in OncoEmr is required";
    if (formData.portal_id === "oncoemr" && isBlank(formData.location_id))
      errors.location_id = "Location ID in OncoEmr is required";
    if ((!isEditMode || isPasswordEditing) && isBlank(formData.password))
      errors.password = "Password is required";
    if (
      formData.mfa_present === "true" &&
      mfaMethod === "secret_key" &&
      (!isEditMode || isSecretKeyEditing) &&
      isBlank(formData.secret_key)
    )
      errors.secret_key = "Secret Key is required";
    if (
      formData.mfa_present === "true" &&
      mfaMethod === "otp_email" &&
      isBlank(formData.email)
    )
      errors.email = "Email is required";
    if (formData.mfa_present !== "true") {
      const maxConcurrency = Number(formData.max_concurrent);
      if (
        isBlank(formData.max_concurrent) ||
        !Number.isInteger(maxConcurrency) ||
        maxConcurrency < 1
      )
        errors.max_concurrent = "Max Concurrency must be a positive integer";
    }
    if (isBlank(formData.mfa_present))
      errors.mfa_present = "Select whether MFA is present";
    if (isBlank(formData.is_active))
      errors.is_active = "Select whether this credential is active";
    return errors;
  }, [formData, isEditMode, isPasswordEditing, isSecretKeyEditing, mfaMethod]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  /**
   * Returns the error message for a field only if it has been touched,
   * otherwise returns `undefined` so the input renders in its neutral state.
   */
  const errorFor = useCallback(
    (field: string) => (touched[field] ? fieldErrors[field] : undefined),
    [touched, fieldErrors],
  );

  /**
   * Marks every currently-invalid field touched. Called from the save
   * handler so all errors light up at once on a failed submit.
   */
  const markAllTouched = useCallback(() => {
    markFieldsTouched(Object.keys(fieldErrors));
  }, [markFieldsTouched, fieldErrors]);

  /**
   * Convenience wrapper used by every required InputField's onChange:
   * updates form state and immediately marks the field touched so the
   * error message (if any) is visible on the very first keystroke.
   */
  const handleFieldChange = useCallback(
    (field: string, value: string) => {
      handleInputChange(field, value);
      markTouched(field);
    },
    [handleInputChange, markTouched],
  );

  return {
    fieldErrors,
    hasErrors,
    errorFor,
    markAllTouched,
    handleFieldChange,
  };
};
